import { Component, DestroyRef, OnInit, signal, inject, computed, resource, effect, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminAccess, AdminDepartment, GetResourcesResponse, Resource } from '../resource-access.models';
import { ResourceAccessService } from '../resource-access.service';
import { CommonService } from 'src/app/services/common.service';
import { Department } from '../../../menu/menu.model';
import { DepartmentService } from 'src/app/services/department.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MainStore } from 'src/app/common/main.store';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ErrorService } from 'src/app/common/errors/error.service';
import { ModalService } from 'src/app/services/modal.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NgClass, DatePipe } from '@angular/common';

@Component({
    selector: 'app-resource-access-list',
    templateUrl: './resource-access-list.component.html',
    styleUrl: './resource-access-list.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgClass, RouterLink, DatePipe]
})
export class ResourceAccessListComponent implements OnInit {
  // Service injections using inject()
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly commonService = inject(CommonService);
  private readonly departmentService = inject(DepartmentService);
  readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly store = inject(MainStore);
  private readonly toastr = inject(ToastrService);
  private readonly errorService = inject(ErrorService);
  private readonly destroyRef = inject(DestroyRef);

  // UI State Signals
  readonly searchText = signal<string>('');
  readonly showAll = signal<boolean>(false);
  readonly selectedDepartmentResource = signal<string>('');
  readonly currentDepartmentId = signal<string>('');
  readonly addingAdmin = signal<boolean>(false);

  // Resource signals for data fetching
  readonly externalResourcesResource = resource({
    params: () => ({ 
      departmentId: this.currentDepartmentId()
    }),
    loader: async ({ params }) => {
      if (!params.departmentId) return [];
      const response = await this.resourceAccessService.getExternalResources(params.departmentId);
      return response;
    }
  });

  readonly adminResourcesResource = resource({
    params: () => ({ 
      departmentId: this.currentDepartmentId(),
      showAll: this.showAll()
    }),
    loader: async ({ params }) => {
      if (!params.departmentId) return [];
      if (params.showAll) {
        return await this.resourceAccessService.getAllAdminResources();
      } else {
        return await this.resourceAccessService.getAdminResources(params.departmentId);
      }
    }
  });

  readonly departmentResourcesResource = resource({
    params: () => ({ 
      departmentId: this.currentDepartmentId()
    }),
    loader: async ({ params }) => {
      if (!params.departmentId) return [];
      return await this.resourceAccessService.getDepartmentResources(params.departmentId);
    }
  });

  readonly adminDepartmentsResource = resource({
    params: () => ({}),
    loader: async () => {
      return await this.resourceAccessService.getAdminDepartments();
    }
  });

  // Computed signals for filtered data
  readonly filteredExternalResources = computed(() => {
    const resources = this.externalResourcesResource.value() || [];
    const searchTerm = this.searchText().toLowerCase().trim();
    
    if (!searchTerm) return resources;
    
    return resources.filter(x =>
      x.consultantName.toLowerCase().includes(searchTerm) ||
      x.department.toLowerCase().includes(searchTerm)
    );
  });



  readonly filteredAdminResources = computed(() => {
    const resources = this.adminResourcesResource.value() || [];
    const searchTerm = this.searchText().toLowerCase().trim();
    
    // Read sorting signals to establish reactive dependency
    const sortBy = this.sortBy();
    const sortDirection = this.sortDirection();
    
    let filteredResources: AdminAccess[];
    
    if (!searchTerm) {
      filteredResources = resources;
    } else {
      const isAdmin = this.store.currentUser.get().isAdmin;
      filteredResources = resources.filter(x =>
        x.resourceName.toLowerCase().includes(searchTerm) ||
        x.customerName.toLowerCase().includes(searchTerm) ||
        x.customerId.toString().includes(searchTerm) ||
        x.initials.toLowerCase().includes(searchTerm) ||
        (isAdmin && x.departmentName.toLowerCase().includes(searchTerm))
      );
    }
    
    return this.sortAdminResources(filteredResources);
  });

  // Computed signal for department resources (used by selectedDepartmentResource effect)
  readonly departmentResources = computed(() => {
    return this.departmentResourcesResource.value() || [];
  });

  // Computed signal for admin departments
  readonly adminDepartments = computed(() => {
    return this.adminDepartmentsResource.value() || [];
  });

  // Loading state computed signals
  readonly isLoadingExternal = computed(() => this.externalResourcesResource.isLoading());
  readonly isLoadingAdmin = computed(() => this.adminResourcesResource.isLoading());
  readonly departmentResourceLoading = computed(() => this.departmentResourcesResource.isLoading());
  readonly loadingError = computed(() => 
    !!this.externalResourcesResource.error() || 
    !!this.adminResourcesResource.error()
  );
  readonly departmentResourceError = computed(() => !!this.departmentResourcesResource.error());

  // Sorting state as signals
  readonly sortBy = signal<'assigned' | 'customerId' | 'customerName' | 'resourceName'>('assigned');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  
  // Category state
  category: 'external' | 'admin' = 'external';

  // Helper method for sorting admin resources
  private sortAdminResources(resources: AdminAccess[]): AdminAccess[] {
    const direction = this.sortDirection() === 'asc' ? 1 : -1;
    const sortByValue = this.sortBy();
    
    return [...resources].sort((a, b) => {
      if (sortByValue === 'assigned') {
        return (Date.parse(a.assigned) - Date.parse(b.assigned)) * direction;
      } else if (sortByValue === 'customerId') {
        return (a.customerId - b.customerId) * direction;
      } else if (sortByValue === 'customerName') {
        return a.customerName.localeCompare(b.customerName) * direction;
      } else if (sortByValue === 'resourceName') {
        return a.resourceName.localeCompare(b.resourceName) * direction;
      }
      return 0;
    });
  }

  ngOnInit(): void {
    const searchTerm = this.route.snapshot.paramMap.get('searchterm');
    const decodedSearchTerm = decodeURIComponent(searchTerm ?? '');
    if (decodedSearchTerm.trim()) {
      this.searchText.set(decodedSearchTerm);
    }

    // Subscribe to department changes and update our signal
    this.commonService.DepartmentChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (department: Department) => {
        this.currentDepartmentId.set(department.id);
        // Reset UI state when department changes
        this.showAll.set(false);
        this.selectedDepartmentResource.set('');
      }
    );

    // Set initial department
    const selectedDepartment = this.departmentService.getDepartment();
    if (selectedDepartment != null) {
      this.currentDepartmentId.set(selectedDepartment.id);
    }


  }

  sortTable(sortBy: 'assigned' | 'customerId' | 'customerName' | 'resourceName'): void {
    // Toggle sort direction
    if (this.sortBy() === sortBy) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortDirection.set('asc');
    }
    // Change sort column
    this.sortBy.set(sortBy);
    
    // The sorting will automatically update through the computed signal
    // No need to manually update arrays anymore
  }

  showMyResources(): void {
    const currentUser = this.store.currentUser.get();
    if (currentUser?.name) {
      this.searchText.set(currentUser.name);
    }
  }

  onSelectedDepartmentResourceChange(resourceGuid: string): void {
    this.selectedDepartmentResource.set(resourceGuid);
    
    if (resourceGuid) {
      const departmentResourcesList = this.departmentResources();
      const selectedResource = departmentResourcesList.find(x => x.resourceGuid === resourceGuid);
      const resourceName = selectedResource?.resourceName ?? '';
      if (resourceName) {
        this.searchText.set(resourceName);
      }
    }
  }

  async onCreateAdminResourceSelect(resourceGuid: string): Promise<void> {
    if (!resourceGuid) return;
    
    const departmentResourcesList = this.departmentResources();
    const selectedResource = departmentResourcesList.find(x => x.resourceGuid === resourceGuid);
    const resourceName = selectedResource?.resourceName ?? 'Unknown';
    
    const confirmed = await this.commonService.confirm(
      'Opret administrator adgang',
      `Er du sikker på at du vil oprette administrator adgang for ${resourceName} til alle afdelingens kunder?`,
      'Ja, opret',
      'Annuller',
      'lg'
    );
    
    if (confirmed) {
      await this.addAdminAccess(resourceGuid);
    }
    
    // Reset the dropdown selection
    this.selectedDepartmentResource.set('');
  }

  //#region Resource Management - Now handled by Resource Signals

  // Trigger reload of all resources
  reloadAllResources(): void {
    this.externalResourcesResource.reload();
    this.adminResourcesResource.reload();
    this.departmentResourcesResource.reload();
  }

  // Legacy method kept for external resource reload specifically
  getExternalResources(): void {
    this.externalResourcesResource.reload();
  }

  // Legacy method kept for admin resource reload specifically  
  getAdminResources(): void {
    this.adminResourcesResource.reload();
  }

  //#endregion

  //#region Search - Now handled by Computed Signals

  // The search is now handled automatically by computed signals
  // This method is kept for compatibility but doesn't need to do anything
  performSearch(searchTerm: string): void {
    // Search is now reactive through computed signals
    // No manual filtering needed
  }

  search(): void {
    // The search happens automatically through computed signals
    // when searchText changes
  }

  //#endregion


  async ToggleResourceExternal(resource: Resource): Promise<void> 
  {
    // Hvis brugeren er en konsulent uden faste kundetilknytning 
    if (resource.role === 4) {
      return;
    }
    resource.showCustomers = !resource.showCustomers;

    if (resource.customers.length > 0) {
      return;
    }


    resource.loadingCustomers = true;
    if (resource.showCustomers) {
      try {
        resource.customers = await this.resourceAccessService.getExternalResourceAccesses(resource.consultantGuid);

      } catch (error) {
        console.error('Failed to fetch customers', error);
      }
    }
    resource.loadingCustomers = false;
  }



  GetAdminDepartmentTitle(department: AdminDepartment) {
    return [department.departmentName, "", ...department.members].join('\n');
  }

  navigateToCustomerDashboard(customerId: number): void {
    this.router.navigate(['/customer-dashboard', customerId]);
  }




  // Admin access

  async deleteResource(resource: AdminAccess): Promise<void> {
    var response = await this.commonService.confirm(
      'Slet admin ressource',
      'Er du sikker på at du vil slette denne admin ressource? Admin ressourcen kan genskabes ved at tilføje ressourcen igen.');
    if (!response) return;
    this.resourceAccessService.deleteAdminResource(resource.resourceGuid).then((result) => {
      if (result.slettet) {
        // Reload admin resources to reflect the deletion
        this.adminResourcesResource.reload();
        this.toastr.success('Admin ressource slettet.', 'Success');
      } else {
        this.errorService.notify('Admin ressource blev ikke slettet', { title: 'Fejl' });
      }
    });
  }

  async resetPassword(resource: AdminAccess): Promise<void> {
    var response = await this.commonService.confirm(
      'Nulstil adgangskode',
      'Er du sikker på at du vil nulstille adgangskoden for denne admin ressource?');
    if (!response) return;
    this.resourceAccessService.resetAdminPassword(resource.resourceGuid).then((result) => {
      console.log(result);

      resource.temporaryPassword = result.temporaryPassword;
      this.toastr.success(`Adgangskode nulstillet for ${resource.resourceName}`, 'Success');
    }).catch((error: HttpErrorResponse) => {
      console.log(error);
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    });
  }

  async addAdminAccess(resourceGuid: string): Promise<void> {
    if (resourceGuid === '') return;

    try {
      this.addingAdmin.set(true);
      let result = await this.resourceAccessService.postAdminResource(resourceGuid);

      if (result.length > 0) {
        this.toastr.success('Admin ressource tilføjet.', 'Success');
        // Reload admin resources to show the new resource
        this.adminResourcesResource.reload();
      } else {
        this.toastr.warning('Ingen admin ressource kunne oprettes', 'Advarsel');
      }

    } catch (error: any) {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    }
    this.addingAdmin.set(false);
  }

  async showUserAccess()
  {
    const reload = await this.modalService.showUserAccessModal();    
    if (reload)
    {
      this.getExternalResources();
    }
  }

  deleteAccessForConsultant(resource: Resource): void {
    this.commonService.confirm('Slet adgang for ' + resource.consultantName + '?', 
                              'Bemærk: Hvis '+ resource.consultantName + ' er sat som implementeringsansvarlig på kunde, så gives der stadig adgang.'
                              , 'Ja', 'Nej','lg')
    .then(async (result) => {
      if (result) {
         await this.resourceAccessService.RemoveUser(resource.consultantGuid);
         this.getExternalResources();
      }
    });
  }

}
